import {cnb} from 'cnbuilder';
import React, {memo, useEffect} from 'react';
import type {ReactNode} from 'react';
import {useTranslation} from 'react-i18next';
import {useSelector, useDispatch} from 'react-redux';
import {NavLink as RouterLink} from 'react-router-dom';
import mixpanel from 'mixpanel-browser';
import UserAPI from '@/user/user-oneapp-api';
import {getCommonRouteById} from '@/router/routes';
import type {
    BrandedFooterField,
    FooterFields,
    RootState,
} from '@/types/root-state';
import {
    CONSENT_AGREEMENT_URL,
    FOOTER_CONTENT_STATE,
    NAV_MENU,
    PRIVACY_PRACTICES_URL,
    SITEMAP,
} from '@/config';
import css from './Footer.scss';

const footerLabels = {
    consent_agreement_url: 'footer.consentAgreement',
    privacy_practices_url: 'footer.privacyPractices',
    terms_of_service_url: 'footer.terms',
    terms_of_use_url: 'footer.termsOfUse',
    terms_of_use: 'footer.termsOfUse',
    non_discrimination_url: 'footer.nonDiscrimination',
    sitemap: 'footer.sitemap',
    legal_disclaimer: 'footer.legal',
    notice_of_privacty_practices: 'footer.noticeOfPrivacyPractices',
    web_and_mobile_privacy_policy: 'footer.webAndMobilePrivacy',
    accessibility: 'footer.accessibility',
} as const;

type FooterLabelKey = keyof typeof footerLabels;

type FooterLinkField = {
    label: string;
    path: string;
    key?: string;
    icon?: ReactNode;
    order?: number;
};

type AppUpdatePayload = Record<string, unknown>;

// TODO: replace with `appUpdate` from `@teladoc/fe-ccm/ui/app/app-actions` once app-actions.js is converted to TypeScript
const createAppUpdate = (payload: AppUpdatePayload) => ({
    type: 'APP_UPDATE' as const,
    payload,
});

const Footer = () => {
    const {oneAppActiveProgram, branding, isImpersonation} = useSelector<
        RootState,
        RootState['app']
    >((state: RootState) => state.app);
    const {userLocale, impersonationInfo} = useSelector<
        RootState,
        RootState['user']
    >((state: RootState) => state.user);
    const {t} = useTranslation();
    const {footer_fields: {brand: brand_fields, default: default_fields} = {}} =
        branding ?? {};
    const dispatch = useDispatch();
    const loggedInMemberId = (
        UserAPI as {
            getMemberId: () => string;
        }
    ).getMemberId();
    const runtimeWindow = window as Window & {
        process?: {env?: Record<string, string | undefined>};
    };
    const version =
        runtimeWindow.process?.env?.VERSION ?? process.env.VERSION ?? '';
    const activeStatePath = document.location.pathname;

    useEffect(() => {
        if (activeStatePath.includes('condition-management')) {
            dispatch(
                createAppUpdate({
                    oneAppActiveProgram:
                        FOOTER_CONTENT_STATE.ConditionManagement.id,
                })
            );
        } else if (activeStatePath.includes('mental-health')) {
            dispatch(
                createAppUpdate({
                    oneAppActiveProgram: FOOTER_CONTENT_STATE.MyStrength.id,
                })
            );
        } else {
            dispatch(
                createAppUpdate({
                    oneAppActiveProgram: FOOTER_CONTENT_STATE.TeleMed.id,
                })
            );
        }
    }, [activeStatePath, oneAppActiveProgram, dispatch]);

    const onClickHandler = (property: string) => {
        mixpanel.track('footer.button.clicked', {
            button: property,
        });
    };

    const handleSitemapClick = () => {
        onClickHandler('Sitemap');
        dispatch(
            createAppUpdate({
                oneAppActiveNav: NAV_MENU.Sitemap.id,
            })
        );
    };

    const renderLink = (footerField: FooterLinkField): ReactNode => {
        const {label, key, path, icon} = footerField;

        if (!label || !path) {
            return null;
        }

        if (key === SITEMAP || path === '/sitemap') {
            return (
                <RouterLink
                    key={key ?? path}
                    className={css.link}
                    to={path}
                    onClick={handleSitemapClick}
                >
                    {icon}
                    {t(label)}
                </RouterLink>
            );
        }

        return (
            <a
                key={key ?? path}
                href={path}
                target="_blank"
                rel="noopener noreferrer"
                className={css.link}
                onClick={() => {
                    onClickHandler(key?.split('.').pop() ?? label);
                }}
                aria-label={`${t(label)}, ${t('screenReader.externalWebPage')}`}
            >
                {icon}
                {t(label)}
            </a>
        );
    };

    const generateFooterLinks = (fields: FooterFields): ReactNode[] => {
        const orderedFooterLabels = (
            Object.keys(footerLabels) as FooterLabelKey[]
        ).map((key, index) => ({
            key,
            value: footerLabels[key],
            order: index,
        }));

        const labels = Object.keys(fields)
            .map((key): FooterLinkField | null => {
                const footerLabel = orderedFooterLabels.find(
                    ofl => ofl.key === key
                );

                if (footerLabel) {
                    const label = footerLabel.value;
                    let path = '';
                    let icon: ReactNode | undefined;
                    const order = footerLabel.order;
                    const value = fields[key];

                    switch (key) {
                        case 'sitemap': {
                            path = getCommonRouteById('sitemap').path;
                            break;
                        }
                        case 'consent_agreement_url': {
                            const consentAgreementUrls =
                                CONSENT_AGREEMENT_URL as Record<string, string>;
                            path = userLocale
                                ? consentAgreementUrls[userLocale] ?? ''
                                : '';
                            break;
                        }
                        case 'privacy_practices_url': {
                            const privacyPracticesUrls =
                                PRIVACY_PRACTICES_URL as Record<string, string>;

                            path = privacyPracticesUrls[userLocale ?? ''] ?? '';
                            break;
                        }
                        default: {
                            path = `${value ?? ''}?locale=${userLocale ?? ''}`;
                        }
                    }

                    return {
                        key,
                        label,
                        path,
                        icon,
                        order,
                    };
                }

                return null;
            })
            .filter((label): label is FooterLinkField => Boolean(label));

        return labels
            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
            .map(renderLink);
    };

    const generateBrandedLinks = (
        fields: BrandedFooterField[]
    ): ReactNode[] => {
        if (!Array.isArray(fields)) {
            return [];
        }

        return fields.map(({label, url}) => {
            if (!label || !url) {
                return null;
            }

            return renderLink({label, path: url, key: url});
        });
    };

    return (
        <footer className={css.root} data-testid="footer-nav-menu">
            <div className={cnb(css.container, css.privateContainer)}>
                <div className={css.footerTitle}>
                    <div className={css.company}>
                        &copy; {branding?.organization}
                    </div>
                    <div className={css.bullet}>&bull;</div>
                    <div className={css.version}>
                        {t('footer.version')} {version}
                    </div>
                </div>
                {oneAppActiveProgram === FOOTER_CONTENT_STATE.MyStrength.id && (
                    <div className={css.contentMyS}>
                        <p>
                            {t('footer.myStrengthContent')}
                            <a
                                className={css.contact}
                                href="tel:988"
                                aria-label={t('footer.firstPhoneNumber')}
                                onClick={() => {
                                    onClickHandler('988');
                                }}
                            >
                                988
                            </a>
                            &nbsp;
                            {t('footer.or')}
                            &nbsp;
                            <a
                                className={css.contact}
                                href="tel:800-273-8255"
                                aria-label={t('footer.secondPhoneNumber')}
                                onClick={() => {
                                    onClickHandler('800-273-8255');
                                }}
                            >
                                800-273-8255.
                            </a>
                        </p>
                    </div>
                )}
                <div>
                    {default_fields && (
                        <p className={css.links}>
                            {generateFooterLinks(default_fields)}
                        </p>
                    )}
                    {brand_fields && (
                        <p className={css.links}>
                            {generateBrandedLinks(brand_fields)}
                        </p>
                    )}
                </div>
            </div>
            {isImpersonation && (
                <div className={css.impersonationFooter}>
                    <span>
                        Impersonator ID: {impersonationInfo?.impersonatorId}
                    </span>
                    <span>
                        Impersonator type: {impersonationInfo?.impersonatorType}
                    </span>
                    <span>Impersonated ID: {loggedInMemberId}</span>
                    <span>
                        Impersonated type: {impersonationInfo?.impersonatedType}
                    </span>
                </div>
            )}
        </footer>
    );
};

// No props from Root; memo skips re-renders when only the parent updates.
export default memo(Footer);
